import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useItem } from '../api/queries'
import type { Area, Dashboard, EquipmentRow, Plant } from '../api/types'
import { BandDonut, HatchDefs, Sparkline, TrendLine } from '../components/charts'
import { DataTable, type Column } from '../components/DataTable'
import { DateRangeFilter } from '../components/DateRangeFilter'
import { Icon } from '../components/Icon'
import { BandBadge, Breadcrumbs, Card, ErrorState, KpiCard, LoadingState, PageHeader, PillFilter, RouteCriticalBadge, Segmented, StalenessBanner } from '../components/ui'
import { overviewPath, plantPath, scoreHistory } from '../features/dashboard/drilldown'
import { SNAPSHOT_DATE, bandDonut, filtersToSearch, groupTrend, isSnapshot, kpiCards, trendSeries, type TrendGrain } from '../features/dashboard/models'
import { EquipmentListFilters } from '../features/dashboard/EquipmentListFilters'
import { useDashboardFilters } from '../features/dashboard/useDashboardFilters'
import { areaLabel } from '../lib/areas'
import { formatScore } from '../lib/bands'
import { formatDate } from '../lib/format'

function columns(): Column<EquipmentRow>[] {
  return [
    { key: 'name', header: 'Equipo', cell: (r) => <><strong>{r.name}</strong> {r.is_route_critical ? <RouteCriticalBadge /> : null}<div className="muted mono">{r.code}</div></> },
    { key: 'score', header: 'Puntaje', cell: (r) => <BandBadge band={r.band} total={r.total} size="sm" />, align: 'right' },
    { key: 'date', header: 'Última inspección', cell: (r) => formatDate(r.inspected_on) },
    {
      key: 'history', header: 'Historial de puntaje', cell: (r) => {
        const h = scoreHistory(r.history)
        return (
          <span className="spark-cell">
            <Sparkline points={h.points} />
            <span className="sr-only">{h.summary}</span>
          </span>
        )
      },
      priority: 'low',
    },
    {
      key: 'delta', header: 'Cambio', align: 'right', cell: (r) => {
        const h = scoreHistory(r.history)
        // A higher score is worse: a rise is shown as a warning, a drop as an improvement.
        const tone = h.delta === null || h.delta === 0 ? undefined : h.delta > 0 ? 'danger' : 'ok'
        return <span className="mono delta" data-tone={tone} title="Cambio desde la inspección anterior">{h.deltaLabel}</span>
      },
    },
  ]
}

/** Area level of the dashboard: every equipment of one area with its current score and its score history. */
export function AreaDashboardPage() {
  const params = useParams()
  const plantId = Number(params.plantId)
  const areaId = Number(params.areaId)
  const navigate = useNavigate()
  const { filters, set } = useDashboardFilters()
  const [grain, setGrain] = useState<TrendGrain>('month')
  const scope = { plant: plantId, area: areaId, date_from: filters.dateFrom, date_to: filters.dateTo }
  const plant = useItem<Plant>(`/api/plants/${plantId}/`)
  const area = useItem<Area>(`/api/areas/${areaId}/`)
  const dash = useItem<Dashboard>('/api/dashboard/', scope)
  const rows = useItem<EquipmentRow[]>('/api/equipment-status/', { ...scope, history: 'true', band: filters.band, search: filters.search })
  const listFiltered = Boolean(filters.band || filters.search)
  const resetList = () => set({ ...filters, band: undefined, search: undefined }, { replace: false })
  const emptyFiltered = listFiltered && !rows.isPending && !rows.isError && rows.data?.length === 0
  const snapshot = isSnapshot(filters)
  const areaName = area.data ? areaLabel(area.data.code, area.data.name) : 'Área'

  return (
    <div className="page">
      <Breadcrumbs items={[
        { label: 'Panel', to: overviewPath(filters) },
        { label: plant.data?.name ?? 'Planta', to: plantPath(plantId, filters) },
        { label: areaName },
      ]} />
      <PageHeader
        title={areaName}
        subtitle={filters.dateTo ? `Equipos del área: inspección más reciente hasta el ${formatDate(filters.dateTo)}` : 'Equipos del área: puntaje actual e historial'}
        actions={
          <div className="filters">
            <PillFilter active={snapshot} onClick={() => set({ ...filters, dateFrom: undefined, dateTo: snapshot ? undefined : SNAPSHOT_DATE })}>Corte del informe (dic 2024)</PillFilter>
            <DateRangeFilter value={{ from: filters.dateFrom, to: filters.dateTo }} onChange={(r) => set({ ...filters, dateFrom: r.from, dateTo: r.to })} />
          </div>
        }
      />
      {dash.isPending ? <LoadingState label="Cargando el área…" /> : dash.isError ? <ErrorState error={dash.error} onRetry={() => void dash.refetch()} title="No se pudo cargar el área" /> : (
        <>
          <HatchDefs />
          {dash.data.latest_inspection_on ? <StalenessBanner inspectedOn={dash.data.latest_inspection_on} /> : null}
          <section className="kpi-row" aria-label="Cifras clave">
            {kpiCards(dash.data.kpis).map((k) => <KpiCard key={k.key} label={k.label} value={k.value} caption={k.caption} tone={k.tone} />)}
          </section>
          <div className="grid grid--2-1">
            <Card title="Tendencia del puntaje promedio" actions={<Segmented small label="Agrupar por" value={grain} onChange={setGrain} options={[{ value: 'month', label: 'Mes' }, { value: 'quarter', label: 'Trimestre' }, { value: 'year', label: 'Año' }]} />}>
              <TrendLine points={groupTrend(trendSeries(dash.data.score_trend).points, grain)} highlight={{ from: filters.dateFrom, to: filters.dateTo }} />
            </Card>
            <Card title="Proporción por banda"><BandDonut model={bandDonut(dash.data.band_distribution)} /></Card>
          </div>
        </>
      )}
      <Card title="Equipos" actions={<span className="faint small">Puntaje promedio del área: <b className="mono">{formatScore(dash.data?.by_area[0]?.avg_score)}</b></span>}>
        <p className="faint small">La condición y la búsqueda solo filtran esta lista; los indicadores y gráficos siguen mostrando toda el área con las fechas elegidas.</p>
        <EquipmentListFilters filters={filters} onChange={(next) => set(next, { replace: false })} onReset={resetList} showReset={listFiltered && !emptyFiltered} />
        <DataTable caption="Equipos del área" columns={columns()} rows={rows.data} loading={rows.isPending} error={rows.error} onRetry={() => void rows.refetch()}
          rowKey={(r) => r.equipment} onRowClick={(r) => navigate(`/assets/${r.equipment}?tab=inspections${filters.dateFrom || filters.dateTo ? `&${filtersToSearch({ dateFrom: filters.dateFrom, dateTo: filters.dateTo })}` : ''}`)} rowLabel={(r) => `Abrir ${r.name}`}
          emptyTitle={listFiltered ? 'Sin equipos que coincidan' : 'Esta área no tiene equipos'}
          emptyHint={listFiltered ? 'Prueba otra condición o búsqueda, o limpia los filtros de equipos.' : 'Los equipos se agregan en Activos o al importar las fichas.'}
          emptyAction={listFiltered ? <button type="button" className="btn" onClick={resetList}>Limpiar filtros de equipos</button> : undefined} />
        <p className="faint small"><Icon name="info" size={14} /> Un puntaje más alto es peor; el cambio compara con la inspección anterior.</p>
      </Card>
    </div>
  )
}
